/**
 * Hand-rolled SSE consumer for the Scenarios page's agent chat.
 *
 * Ported near-verbatim from `capacity/stream.ts` — same `fetch` + `data: <json>\n\n` framing,
 * same same-origin cookie auth, same "throw on a bad response, dispatch per event after that"
 * shape, same endpoint (`POST /api/agent/capacity/messages`). What changed is the wire shape of
 * the scenario itself: the five-lever `ScenarioState`/`ScenarioPatch` (`relo_threshold`,
 * `pack_count`, `conv_count`, `cap_system`, `cap_units`, `pace_pct`, `agent_set`) replacing the
 * old four-lever `active_levers`/`removed_families`/`roll`. Every type below is a hand-written
 * mirror of the backend's `api/models.py` — kept in sync by hand until this gets a generated
 * client.
 */

import { logger } from "@/lib/logger";

/** Thrown when the POST itself fails (before any SSE frame arrives) — carries the HTTP status
 *  so callers can tell "agent not deployed yet" (501/503) apart from a generic network error. */
export class AgentStreamError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AgentStreamError";
    this.status = status;
  }
}

/** Mirrors the backend's `ScenarioState` — the wire shape of the current scenario, sent with
 *  every turn since the endpoint is stateless. */
export interface ScenarioStatePayload {
  year: number;
  relo_threshold: number;
  relo_excluded: string[];
  pack_count: number;
  pack_excluded: string[];
  conv_count: number;
  conv_excluded: string[];
  cap_system: "aframe" | "p2l" | "perfect" | null;
  cap_units: number;
  pace_pct: number;
  agent_set: string[];
}

/** Mirrors the backend's `ScenarioPatch` — every field optional, absolute (not relative)
 *  values. `cap_system` is only ever WRITTEN to a chosen system, never cleared back to `null` —
 *  turning the capital lever off is always `cap_units: 0` (see `api/models.py`'s docstring). */
export interface ScenarioPatch {
  year?: number;
  relo_threshold?: number;
  relo_excluded?: string[];
  pack_count?: number;
  pack_excluded?: string[];
  conv_count?: number;
  conv_excluded?: string[];
  cap_system?: "aframe" | "p2l" | "perfect" | null;
  cap_units?: number;
  pace_pct?: number;
  agent_set?: string[];
}

export interface ScenarioHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

/** The agent's own words about what it's doing mid-turn — mirrors `NarrationRecord`. */
export interface ScenarioNarration {
  id: string;
  text: string;
  text_offset?: number;
}

interface TextDeltaEvent {
  type: "text_delta";
  text: string;
}
interface StatusEvent {
  type: "status";
  status?: string;
}
interface NarrationDeltaEvent {
  type: "narration_delta";
  id: string;
  text: string;
}
interface NarrationEvent {
  type: "narration";
  narration: ScenarioNarration;
}
interface ToolCallEvent {
  type: "tool_call";
  tool_call: unknown;
}
interface ScenarioUpdateEvent {
  type: "scenario_update";
  patch: ScenarioPatch;
}
interface ErrorEvent {
  type: "error";
  message: string;
}

/** A follow-up prompt the agent thinks is worth surfacing after this turn — `label` is the short
 *  pill text, `message` is the fuller question actually sent when the chip is clicked. */
export interface CapacitySuggestion {
  label: string;
  message: string;
}

interface SuggestionsEvent {
  type: "suggestions";
  suggestions: CapacitySuggestion[];
}

export interface StreamScenarioAgentHandlers {
  onTextDelta?: (text: string) => void;
  onStatus?: (status: string | undefined) => void;
  /** Append this chunk to the narration with this id; a new id starts a new line. Always
   *  followed by `onNarration` for the same id once the call completes. */
  onNarrationDelta?: (id: string, text: string) => void;
  /** Authoritative: replaces whatever the deltas for this id built up. */
  onNarration?: (narration: ScenarioNarration) => void;
  onToolCall?: (toolCall: unknown) => void;
  /** A scenario field changed — apply it to the reducer immediately so the dashboard updates
   *  live, mid-stream. */
  onScenarioUpdate?: (patch: ScenarioPatch) => void;
  /** Fresh suggestion chips for the next turn, sent once per successful reply just before `done`. */
  onSuggestions?: (suggestions: CapacitySuggestion[]) => void;
  onDone?: () => void;
  onError?: (message: string) => void;
}

/**
 * POSTs the user's message plus the live scenario and dispatches each parsed SSE event to the
 * matching handler. Resolves once the stream ends. Throws `AgentStreamError` if the request
 * itself fails (non-2xx, or no body to read) — callers should catch that and treat it the same
 * as a mid-stream `error` event.
 */
export async function streamScenarioAgent(
  content: string,
  scenario: ScenarioStatePayload,
  history: ScenarioHistoryMessage[],
  handlers: StreamScenarioAgentHandlers,
  signal?: AbortSignal,
): Promise<void> {
  logger.debug("[scenario-agent-stream] send", content);
  const response = await fetch("/api/agent/capacity/messages", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, scenario, history }),
    signal,
  });

  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => "");
    throw new AgentStreamError(
      response.status,
      `scenario agent stream failed (${response.status})${detail ? `: ${detail}` : ""}`,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) {
          continue;
        }
        const payload = line.slice(5).trim();
        if (payload) {
          dispatch(payload, handlers);
        }
      }
    }
  }
}

function dispatch(payload: string, handlers: StreamScenarioAgentHandlers): void {
  let event: { type?: string };
  try {
    event = JSON.parse(payload);
  } catch (err) {
    logger.warn("[scenario-agent-stream] malformed event payload", err);
    return;
  }
  switch (event.type) {
    case "text_delta":
      handlers.onTextDelta?.((event as TextDeltaEvent).text);
      break;
    case "status":
      handlers.onStatus?.((event as StatusEvent).status);
      break;
    case "narration_delta": {
      const delta = event as NarrationDeltaEvent;
      handlers.onNarrationDelta?.(delta.id, delta.text);
      break;
    }
    case "narration":
      handlers.onNarration?.((event as NarrationEvent).narration);
      break;
    case "tool_call":
      handlers.onToolCall?.((event as ToolCallEvent).tool_call);
      break;
    case "scenario_update":
      handlers.onScenarioUpdate?.((event as ScenarioUpdateEvent).patch);
      break;
    case "suggestions":
      handlers.onSuggestions?.((event as SuggestionsEvent).suggestions);
      break;
    case "done":
      handlers.onDone?.();
      break;
    case "error":
      handlers.onError?.((event as ErrorEvent).message);
      break;
    default:
      logger.warn("[scenario-agent-stream] unknown event type", event.type);
  }
}
